import type { NavItem } from "../app/sidebar"
import {
	ACCOUNT_NAV_ITEMS,
	FEATURES_NAV_ITEMS,
	INTEGRATIONS_NAV_ITEMS,
	SETTINGS_SUBPAGE_PARENT_BY_HREF,
	SETUP_NAV_ITEMS,
	WORKSPACE_NAV_ITEMS,
} from "./sidebar"

// Lookup of every top-level settings nav item by href, used to derive the
// "back to parent" link for nested settings routes. Integration pages aren't
// listed, so an integration page itself resolves up to "Integrations".
const NAV_ITEMS_BY_HREF: Record<string, NavItem> = Object.fromEntries(
	[
		...WORKSPACE_NAV_ITEMS,
		...ACCOUNT_NAV_ITEMS,
		...SETUP_NAV_ITEMS,
		...FEATURES_NAV_ITEMS,
	].map((item) => [item.href, item]),
)

// Only consulted for ancestors, so a page nested under an integration (Slack's
// Channels page) goes back to that integration.
const INTEGRATION_ITEMS_BY_HREF: Record<string, NavItem> = Object.fromEntries(
	INTEGRATIONS_NAV_ITEMS.map((item) => [item.href, item]),
)

export function findParentNavItem(pathname: string): NavItem | null {
	// Top-level nav items don't need a parent — the sidebar itself is the way back.
	if (NAV_ITEMS_BY_HREF[pathname]) return null

	// A registered subpage names its parent, which segment trimming can't find:
	// /settings/docs-markdown-import lives under /settings/transfer.
	const registeredParent = SETTINGS_SUBPAGE_PARENT_BY_HREF[pathname]
	if (registeredParent) {
		const item = NAV_ITEMS_BY_HREF[registeredParent]
		if (item) return item
	}

	// Walk up by trimming trailing path segments. The first ancestor that
	// matches a sidebar nav item is treated as the parent.
	const segments = pathname.split("/").filter(Boolean)
	for (let i = segments.length - 1; i > 0; i--) {
		const candidate = "/" + segments.slice(0, i).join("/")
		const item =
			NAV_ITEMS_BY_HREF[candidate] ?? INTEGRATION_ITEMS_BY_HREF[candidate]
		if (item) return item
	}
	return null
}
